import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { SendHorizontal, Square } from "lucide-react";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  onStop?: () => void;
  autoFocus?: boolean;
}

export function ChatInput({ onSend, disabled = false, isStreaming = false, onStop, autoFocus = false }: ChatInputProps) {
  const { t } = useTranslation("common");
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 items-end p-3 border-t border-neutral-200 bg-white shrink-0">
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t("chat.placeholder")}
        disabled={disabled || isStreaming}
        rows={1}
        className="flex-1 resize-none rounded-lg border border-neutral-200 px-3 py-2 text-sm placeholder:text-neutral-400
                   focus:outline-none focus:ring-2 focus:ring-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {isStreaming ? (
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="h-9 w-9 shrink-0"
          onClick={onStop}
        >
          <Square className="h-3 w-3 fill-current" />
        </Button>
      ) : (
        <Button
          type="submit"
          size="icon"
          disabled={disabled || !message.trim()}
          className="h-9 w-9 shrink-0"
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      )}
    </form>
  );
}
